import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { useTranslation } from 'react-i18next';
import { qrPayload, qrPoll, qrStart } from '../api/auth';
import type { QrStart } from '../api/auth';

const POLL_MS = 2000;

/**
 * Вход по QR (сценарий WhatsApp): показываем QR, телефон с уже выполненным входом сканирует
 * его и подтверждает. Поллим статус раз в 2 с; QR живёт 90 с — по expired рисуем новый.
 */
export default function QrLogin({ onAuthed }: { onAuthed: () => void }) {
  const { t } = useTranslation('auth');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [start, setStart] = useState<QrStart | null>(null);
  const [error, setError] = useState('');
  // Счётчик перезапусков: смена значения = запросить новый QR.
  const [generation, setGeneration] = useState(0);
  // onAuthed родитель может передавать новой функцией на каждый рендер — не перезапускаем из-за этого поллинг.
  const onAuthedRef = useRef(onAuthed);
  onAuthedRef.current = onAuthed;

  useEffect(() => {
    let alive = true;
    setStart(null);
    setError('');
    qrStart()
      .then((s) => alive && setStart(s))
      .catch(() => alive && setError(t('errors.qrStart')));
    return () => {
      alive = false;
    };
  }, [generation, t]);

  useEffect(() => {
    if (!start || !canvasRef.current) return;
    QRCode.toCanvas(canvasRef.current, qrPayload(start.qrToken), { width: 220, margin: 4, color: { dark: '#000000', light: '#ffffff' } }).catch(() =>
      setError(t('errors.qrRender')),
    );
  }, [start, t]);

  useEffect(() => {
    if (!start) return;
    let alive = true;
    let timer = 0;
    const tick = async () => {
      try {
        const status = await qrPoll(start);
        if (!alive) return;
        if (status === 'confirmed') {
          onAuthedRef.current();
          return;
        }
        if (status === 'expired') {
          setGeneration((g) => g + 1);
          return;
        }
      } catch {
        // Сеть мигнула — просто пробуем ещё раз на следующем тике.
      }
      if (alive) timer = window.setTimeout(tick, POLL_MS);
    };
    timer = window.setTimeout(tick, POLL_MS);
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [start]);

  return (
    <div className="qr-login">
      <div className="qr-login-box">
        {start ? <canvas ref={canvasRef} /> : !error && <span className="spinner" />}
      </div>
      {error ? (
        <>
          <div className="error">{error}</div>
          <button type="button" className="link-btn" onClick={() => setGeneration((g) => g + 1)}>
            {t('qr.retry')}
          </button>
        </>
      ) : (
        <ol className="qr-login-steps">
          <li>{t('qr.stepOne')}</li>
          <li>{t('qr.stepTwo')}</li>
          <li>{t('qr.stepThree')}</li>
        </ol>
      )}
    </div>
  );
}
